import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Bell, CheckCircle2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const Alerts = ({ user }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, resolved

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axiosInstance.get('/alerts');
      setAlerts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli alert');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await axiosInstance.put(`/alerts/${alertId}/resolve`);
      toast.success('Alert risolto!');
      fetchAlerts();
    } catch (error) {
      toast.error('Errore durante la risoluzione');
    }
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo alert?')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/alerts/${alertId}`);
      toast.success('Alert eliminato!');
      fetchAlerts();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const canResolve = user?.role === 'admin' || user?.role === 'maintainer';

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'active') return !alert.resolved;
    if (filter === 'resolved') return alert.resolved;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="alerts-page">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Alert</h1>
        <p className="text-gray-600">Centro notifiche e avvisi</p>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'all'
              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
          data-testid="filter-all"
        >
          Tutti ({alerts.length})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'active'
              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
          data-testid="filter-active"
        >
          Attivi ({alerts.filter(a => !a.resolved).length})
        </button>
        <button
          onClick={() => setFilter('resolved')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === 'resolved'
              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          }`}
          data-testid="filter-resolved"
        >
          Risolti ({alerts.filter(a => a.resolved).length})
        </button>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.length === 0 ? (
          <Card className="shadow-lg border-0">
            <CardContent className="py-12">
              <p className="text-gray-500 text-center" data-testid="no-alerts-message">Nessun alert da visualizzare</p>
            </CardContent>
          </Card>
        ) : (
          filteredAlerts.map((alert) => (
            <Card
              key={alert.id}
              className={`card-hover shadow-lg border-0 ${
                alert.resolved ? 'opacity-60' : ''
              }`}
              data-testid={`alert-card-${alert.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center flex-1">
                    <div
                      className={`p-2 rounded-lg mr-3 ${
                        alert.priority === 'high'
                          ? 'bg-red-100'
                          : alert.priority === 'medium'
                          ? 'bg-orange-100'
                          : 'bg-yellow-100'
                      }`}
                    >
                      {alert.resolved ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <Bell
                          className={`w-5 h-5 ${
                            alert.priority === 'high'
                              ? 'text-red-600'
                              : alert.priority === 'medium'
                              ? 'text-orange-600'
                              : 'text-yellow-600'
                          }`}
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{alert.message}</CardTitle>
                      {alert.equipmentName && (
                        <p className="text-sm text-gray-600 mb-2">{alert.equipmentName}</p>
                      )}
                      <div className="flex gap-2 items-center">
                        <span
                          className={`badge ${
                            alert.priority === 'high'
                              ? 'badge-red'
                              : alert.priority === 'medium'
                              ? 'badge-yellow'
                              : 'badge-blue'
                          }`}
                        >
                          {alert.priority}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(alert.createdAt).toLocaleDateString('it-IT', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      {alert.resolved && alert.resolvedBy && (
                        <p className="text-sm text-green-600 mt-2">
                          Risolto da {alert.resolvedBy} il {new Date(alert.resolvedAt).toLocaleDateString('it-IT')}
                        </p>
                      )}
                    </div>
                  </div>
                  {canResolve && (
                    <div className="flex gap-2 ml-4">
                      {!alert.resolved && (
                        <Button
                          onClick={() => handleResolve(alert.id)}
                          className="bg-green-500 hover:bg-green-600"
                          data-testid={`resolve-alert-${alert.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Risolvi
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDelete(alert.id)}
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-alert-${alert.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Elimina
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Alerts;